import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronRight, UserPlus, Building2 } from "lucide-react";
import { CreateRealtorDialog, CreateAgencyDialog } from "./AdminCreateDialogs";

export default function AdminUsersList() {
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");
  const [showRealtor, setShowRealtor] = useState(false);
  const [showAgency, setShowAgency] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (plan !== "all") params.set("plan", plan);
      if (status !== "all") params.set("status", status);
      const [usersRes, agenciesRes] = await Promise.all([
        api.get(`/admin/users?${params}`),
        api.get(`/admin/agencies`).catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      // Map agency_id -> name for user rows
      const map = {};
      (agenciesRes.data || []).forEach((a) => { map[a.id] = a.name; });
      setAgencies(map);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
  }, [q, plan, status]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-5" data-testid="admin-users-root">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Carteira de clientes</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A2F]">Usuários</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowRealtor(true)} data-testid="btn-new-realtor"
            className="bg-[#1B3A2F] hover:bg-[#0F1F1A] text-white gap-2">
            <UserPlus className="w-4 h-4" /> Novo corretor
          </Button>
          <Button onClick={() => setShowAgency(true)} data-testid="btn-new-agency"
            className="bg-[#D4651A] hover:bg-[#B8551A] text-white gap-2">
            <Building2 className="w-4 h-4" /> Nova imobiliária
          </Button>
        </div>
      </header>

      <CreateRealtorDialog open={showRealtor} onOpenChange={setShowRealtor} onCreated={load} />
      <CreateAgencyDialog open={showAgency} onOpenChange={setShowAgency} onCreated={load} />

      <Card className="p-4 rounded-2xl">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              data-testid="admin-users-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nome, email ou telefone"
              className="pl-9"
            />
          </div>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger data-testid="admin-users-filter-plan"><SelectValue placeholder="Plano" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os planos</SelectItem>
              <SelectItem value="solo">Solo</SelectItem>
              <SelectItem value="dupla">Dupla</SelectItem>
              <SelectItem value="equipe">Equipe</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-users-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="active">Pagantes</SelectItem>
              <SelectItem value="trial">Em trial</SelectItem>
              <SelectItem value="suspended">Suspensos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-stone-500">Carregando…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-stone-500">Nenhum usuário encontrado.</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {users.map((u) => (
              <button
                key={u.id}
                data-testid={`admin-user-row-${u.id}`}
                onClick={() => navigate(`/admin/users/${u.id}`)}
                className="w-full flex items-center gap-4 p-4 hover:bg-stone-50 transition text-left"
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-white ${u.is_active === false ? 'bg-stone-400' : 'bg-[#1B3A2F]'}`}>
                  {u.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm truncate">{u.name}</div>
                    {u.is_active === false && <Badge variant="destructive">Suspenso</Badge>}
                    {u.role === "admin" && <Badge className="bg-[#D4651A] hover:bg-[#D4651A]">admin</Badge>}
                    {u.role === "agency_admin" && <Badge className="bg-[#3B9EFF] hover:bg-[#3B9EFF]">gestor</Badge>}
                    {u.role === "agency_realtor" && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">corretor da imob.</Badge>}
                    {u.plan_paid_at ? (
                      <Badge variant="outline" className="capitalize">{u.plan} pago</Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">trial</Badge>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {u.email} · {u.phone}
                    {u.agency_id && agencies[u.agency_id] && (
                      <span className="ml-2 text-[#3B9EFF]">· {agencies[u.agency_id]}</span>
                    )}
                  </div>
                </div>
                <div className="hidden sm:flex gap-4 text-xs text-stone-500">
                  <span><strong className="text-[#1B3A2F]">{u.leads_count}</strong> leads</span>
                  <span><strong className="text-[#1B3A2F]">{u.properties_count}</strong> imóveis</span>
                  <span><strong className="text-[#1B3A2F]">{u.messages_count}</strong> msgs</span>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
